import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import SaleOrderServices, {
  parseCatalogProducts,
} from "../../../services/SalesOrderService";
import CustomSelect from "../../component/common/customSelect";
import SearchField from "../../component/common/searchField";
import { showToast } from "../../component/common/toastNotification";
import { CURRENCY_SYMBOL } from "../../../constants/currency";
import { OrderData, ProductFilterParams } from "../../../types/saleOrder";
import { CodServedCustomer } from "../../../types/codSalesRep";
import {
  isCustomerIdRequiredError,
  isCustomerNotAuthorizedError,
  isSalesRepCodeConfigError,
  isTradingCustomerError,
  unwrapListPayload,
} from "../../../utils/codUtils";
import {
  NJIM,
  primaryBtnSx,
  secondaryBtnSx,
  quantityStepperSx,
  stickySummarySx,
  glassCardSx,
  codTableSx,
  codTableHeadCellSx,
} from "./codStyles";

import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import CodSectionHeader from "../../../components/ui/CodSectionHeader";

interface CodNewOrderTabProps {
  customer: CodServedCustomer | null;
  onOrderSubmitted?: () => void;
  onCustomerCleared?: () => void;
}

const pickField = (raw: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
};

const normalizeProduct = (raw: Record<string, unknown>) => ({
  productId: String(pickField(raw, "id", "Id") ?? ""),
  name: String(pickField(raw, "name", "Name", "title", "Title") ?? ""),
  category: String(pickField(raw, "category", "Category") ?? ""),
  batch: String(pickField(raw, "batch", "Batch") ?? ""),
  price: Number(pickField(raw, "price", "Price") ?? 0),
  stock: Number(pickField(raw, "stock", "Stock") ?? 0),
  description: String(pickField(raw, "description", "Description") ?? ""),
  code: String(pickField(raw, "code", "Code") ?? ""),
  stockSite: String(
    pickField(raw, "effectiveStockSite", "EffectiveStockSite", "stockSite", "StockSite") ??
      ""
  ),
});

type CodProduct = ReturnType<typeof normalizeProduct>;
type CodCartItem = CodProduct & { quantity: number };

const dedupeByCode = (products: CodProduct[]) => {
  const seen = new Map<string, CodProduct>();
  products.forEach((product) => {
    const key = product.code || product.productId;
    if (!key || seen.has(key)) return;
    seen.set(key, product);
  });
  return Array.from(seen.values());
};

const buildDeliveryDetails = (customer: CodServedCustomer) => {
  const addressParts = [customer.addressDescription1, customer.addressDescription2]
    .map((part) => part?.trim())
    .filter(Boolean);
  if (addressParts.length === 0 && !customer.statGroup3Description && !customer.mobileNumber) {
    return undefined;
  }
  return {
    address: addressParts.join(" - ") || customer.customerName,
    city: customer.statGroup3Description?.trim() || customer.customerCategory || "",
    phone: customer.mobileNumber?.trim() || " ",
    notes: "",
  };
};

const PRODUCT_PAGE_SIZE = 500;
const PRODUCT_MAX_PAGES = 20;

const CodNewOrderTab: React.FC<CodNewOrderTabProps> = ({
  customer,
  onOrderSubmitted,
  onCustomerCleared,
}) => {
  const { t } = useTranslation();
  const categoriesFetched = useRef(false);
  const requestIdRef = useRef(0);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [catalogueBanner, setCatalogueBanner] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [products, setProducts] = useState<CodProduct[]>([]);
  const [cart, setCart] = useState<CodCartItem[]>([]);
  const [categoryData, setCategoryData] = useState<string[]>([]);
  const [stockFilter, setStockFilter] = useState<"inStock" | "outOfStock">("inStock");
  const [filters, setFilters] = useState<ProductFilterParams>({
    keyword: "",
    category: "",
    pageNumber: 1,
    pageSize: PRODUCT_PAGE_SIZE,
    sortDescending: false,
  });

  useEffect(() => {
    if (!categoriesFetched.current) {
      categoriesFetched.current = true;
      SaleOrderServices.getCategoryList()
        .then((res) => setCategoryData(res?.data ?? []))
        .catch(() => undefined);
    }
  }, []);

  const loadFallbackCatalogue = useCallback(
    async (selected: CodServedCustomer): Promise<CodProduct[]> => {
      const response = await SaleOrderServices.getRawProductCatalogue();
      const rows = unwrapListPayload(response?.data).map((row) =>
        normalizeProduct(row)
      );
      const site = selected.onlineShipmentSite?.trim();
      const siteRows = site
        ? rows.filter(
            (row) =>
              row.stockSite.trim().toLowerCase() === site.toLowerCase()
          )
        : [];
      if (site && siteRows.length > 0) {
        return dedupeByCode(siteRows);
      }
      return dedupeByCode(rows);
    },
    []
  );

  const fetchProducts = useCallback(
    async (filterParams: ProductFilterParams, selected: CodServedCustomer) => {
      const requestId = ++requestIdRef.current;
      setProducts([]);
      setLoadingProducts(true);
      setProductsError("");
      setCatalogueBanner("");
      try {
        const collected: CodProduct[] = [];
        for (let page = 1; page <= PRODUCT_MAX_PAGES; page += 1) {
          const params = { ...filterParams, pageNumber: page, pageSize: PRODUCT_PAGE_SIZE };
          delete params.stockStatus;
          const response = await SaleOrderServices.getSaleProductList(
            params,
            selected.id
          );
          if (requestId !== requestIdRef.current) return;
          const payload = response?.data as Record<string, unknown> | undefined;
          const items = parseCatalogProducts(payload).map((row) =>
            normalizeProduct(row)
          );
          collected.push(...items);
          if (items.length < PRODUCT_PAGE_SIZE) break;
        }

        let next = dedupeByCode(collected);
        if (next.length === 0) {
          next = await loadFallbackCatalogue(selected);
          if (requestId !== requestIdRef.current) return;
          setCatalogueBanner(
            selected.onlineShipmentSite
              ? t("codSalesRep.catalogueSiteFallback")
              : t("codSalesRep.catalogueUnscopedBanner")
          );
        }
        if (requestId !== requestIdRef.current) return;
        setProducts(next);
      } catch (error: unknown) {
        if (requestId !== requestIdRef.current) return;
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? "";
        if (isCustomerIdRequiredError(message)) {
          setProductsError(t("codSalesRep.selectCustomerFirst"));
          setProducts([]);
          return;
        }
        if (isTradingCustomerError(message)) {
          setProductsError(t("codSalesRep.notATradingCustomer"));
          setProducts([]);
          onCustomerCleared?.();
          return;
        }
        try {
          const fallback = await loadFallbackCatalogue(selected);
          if (requestId !== requestIdRef.current) return;
          if (fallback.length > 0) {
            setProducts(fallback);
            setCatalogueBanner(t("codSalesRep.catalogueUnscopedBanner"));
            return;
          }
        } catch {
          // fall through
        }
        setProducts([]);
        setProductsError(t("codSalesRep.productsLoadFailed"));
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingProducts(false);
        }
      }
    },
    [loadFallbackCatalogue, onCustomerCleared, t]
  );

  useEffect(() => {
    if (!customer) {
      requestIdRef.current += 1;
      setProducts([]);
      setCart([]);
      setProductsError("");
      setCatalogueBanner("");
      setActiveStep(0);
      return;
    }
    const timer = setTimeout(() => {
      fetchProducts(filters, customer);
    }, 400);
    return () => clearTimeout(timer);
  }, [customer, filters, fetchProducts]);

  const displayedProducts = useMemo(
    () =>
      products.filter((product) =>
        stockFilter === "outOfStock" ? product.stock <= 0 : product.stock > 0
      ),
    [products, stockFilter]
  );

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleAddToCart = (product: CodProduct) => {
    setCart((prev) => {
      const idx = prev.findIndex((item) => item.productId === product.productId);
      if (idx !== -1) {
        return prev.map((item) =>
          item.productId === product.productId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const handleRemoveItem = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  };

  const handleSetQuantity = (productId: string, value: string, stock: number) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1) return;
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId
          ? { ...item, quantity: Math.min(parsed, stock) }
          : item
      )
    );
  };

  const handleIncreaseQuantity = (productId: string, stock: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId && item.quantity < stock
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  const handleDecreaseQuantity = (productId: string) => {
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  };

  const isProductInCart = (productId: string) =>
    cart.some((item) => item.productId === productId);

  const handleSubmitOrder = async () => {
    if (!customer || cart.length === 0) return;
    const deliveryDetails = buildDeliveryDetails(customer);
    const orderPayload: OrderData = {
      date: new Date().toISOString(),
      items: cart.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
      })),
      amount: cartTotal,
      status: 0,
      ...(deliveryDetails ? { deliveryDetails } : {}),
    };

    setSubmitting(true);
    try {
      const response = await SaleOrderServices.postCodSaleOrder(
        orderPayload,
        customer.id
      );
      if (response.status === 200) {
        const data = (response.data ?? {}) as Record<string, unknown>;
        const orderId = String(data.id ?? data.Id ?? "").trim();
        const approvalStatus = Number(data.approvalStatus ?? data.ApprovalStatus ?? 0);
        if (!orderId) {
          showToast(t("errors.failedToSubmitOrder"), "error");
          return;
        }
        showToast(
          approvalStatus === 1
            ? t("codSalesRep.orderSubmittedPending")
            : t("codSalesRep.orderSubmitted"),
          "success"
        );
        setCart([]);
        setActiveStep(0);
        onOrderSubmitted?.();
      }
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: { errors?: Record<string, string[]>; title?: string; message?: string };
        };
      };
      const status = err.response?.status;
      const apiMessage =
        typeof err.response?.data?.message === "string"
          ? err.response.data.message.trim()
          : "";
      if (status === 403) {
        if (isSalesRepCodeConfigError(apiMessage)) {
          showToast(t("codSalesRep.salesRepCodeNotConfigured"), "error");
        } else {
          showToast(
            isCustomerNotAuthorizedError(apiMessage) || apiMessage
              ? apiMessage
              : t("codSalesRep.customerNotAuthorized"),
            "error"
          );
        }
        setCart([]);
        setActiveStep(0);
        onCustomerCleared?.();
        return;
      }
      const validationErrors = err.response?.data?.errors;
      if (validationErrors) {
        const firstMessage = Object.values(validationErrors).flat()[0];
        showToast(firstMessage || t("errors.failedToSubmitOrder"), "error");
      } else {
        showToast(apiMessage || t("errors.failedToSubmitOrder"), "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!customer) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {t("codSalesRep.selectCustomerFirst")}
      </Alert>
    );
  }

  return (
    <Box>
      <CodSectionHeader
        title={t("codSalesRep.createCodOrder")}
        helper={t("codSalesRep.newOrderHint")}
        action={
          <Box
            sx={{
              border: `1px solid ${NJIM.border}`,
              borderRadius: "999px",
              background: NJIM.muted,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 11, padding: "6px 12px", color: NJIM.textSecondary }}>
              {t("common.step")} {activeStep + 1} {t("common.of")} 2
            </Typography>
          </Box>
        }
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 3 }}>
        {[0, 1].map((step) => (
          <Box key={step} sx={{ width: "48%" }}>
            <LinearProgress
              variant="determinate"
              value={activeStep >= step ? 100 : 0}
              sx={{
                height: 6,
                borderRadius: 5,
                backgroundColor: NJIM.border,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  background: NJIM.walletHero,
                },
              }}
            />
          </Box>
        ))}
      </Box>

      {activeStep === 0 && (
        <Box>
          {productsError && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              {productsError}
            </Alert>
          )}
          {catalogueBanner && !productsError && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {catalogueBanner}
            </Alert>
          )}

          <TableContainer sx={{ marginTop: 1 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: NJIM.textPrimary, mb: 0.5 }}>
              {t("salesOrder.selectProducts")}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: NJIM.textMuted, mb: 2 }}>
              {t("salesOrder.browseAndAdd")} — {customer.customerName}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <SearchField
                value={filters.keyword}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, keyword: value, pageNumber: 1 }))
                }
                placeholder={t("salesOrder.searchProducts")}
                sx={{
                  flex: "1 1 200px",
                  minWidth: 200,
                }}
              />
              <Box sx={{ width: { xs: "100%", sm: "200px" } }}>
                <CustomSelect
                  name="categories"
                  placeHolder={t("salesOrder.allCategories")}
                  value={filters.category}
                  onChange={(event: SelectChangeEvent<string | number>) =>
                    setFilters((prev) => ({
                      ...prev,
                      category: event.target.value as string,
                      pageNumber: 1,
                    }))
                  }
                  options={categoryData.map((category) => ({
                    label: category,
                    value: category,
                  }))}
                  resettable
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={stockFilter === "inStock"}
                    onChange={(e) =>
                      setStockFilter(e.target.checked ? "inStock" : "outOfStock")
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: NJIM.ink },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: NJIM.ink,
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: NJIM.ink }}>
                    {stockFilter === "inStock"
                      ? t("salesOrder.inStock")
                      : t("salesOrder.outOfStock")}
                  </Typography>
                }
              />
            </Box>

            {loadingProducts ? (
              <TableSkeleton rows={6} columns={4} />
            ) : (
              <Table sx={codTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={codTableHeadCellSx}>
                      {t("salesOrder.productName")}
                    </TableCell>
                    <TableCell sx={codTableHeadCellSx}>
                      {t("salesOrder.category")}
                    </TableCell>
                    <TableCell align="right" sx={codTableHeadCellSx}>
                      {t("common.price")}
                    </TableCell>
                    <TableCell sx={codTableHeadCellSx}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ border: 0 }}>
                        <EmptyState
                          title={t("codSalesRep.emptyProductsTitle")}
                          description={t("codSalesRep.emptyProductsDesc")}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedProducts.map((product) => (
                      <TableRow key={product.productId} hover>
                        <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>{product.name}</TableCell>
                        <TableCell sx={{ color: NJIM.textSecondary }}>{product.category}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {CURRENCY_SYMBOL}
                          {product.price.toLocaleString("en-US")}
                        </TableCell>
                        <TableCell>
                          {product.stock <= 0 ? (
                            <Button
                              variant="contained"
                              size="small"
                              disabled
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "12px",
                                borderRadius: "8px",
                                background: NJIM.muted,
                                color: NJIM.textMuted,
                                boxShadow: "none",
                                "&.Mui-disabled": {
                                  background: NJIM.muted,
                                  color: NJIM.textMuted,
                                },
                              }}
                            >
                              {t("salesOrder.outOfStock")}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              disabled={isProductInCart(product.productId)}
                              onClick={() => handleAddToCart(product)}
                              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "12px",
                                borderRadius: "8px",
                                background: isProductInCart(product.productId)
                                  ? NJIM.successWash
                                  : NJIM.walletHero,
                                color: isProductInCart(product.productId) ? NJIM.success : NJIM.white,
                                boxShadow: isProductInCart(product.productId)
                                  ? "none"
                                  : NJIM.elevation,
                                "&.Mui-disabled": {
                                  background: NJIM.successWash,
                                  color: NJIM.success,
                                },
                              }}
                            >
                              {isProductInCart(product.productId)
                                ? `✓ ${t("common.added")}`
                                : t("common.add")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {cart.length > 0 && (
              <Box sx={stickySummarySx}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography sx={{ fontWeight: 600, color: NJIM.ink }}>
                    {t("codSalesRep.orderSummary")} ({cart.length})
                  </Typography>
                  <Typography sx={{ fontWeight: 800, color: NJIM.ink, fontVariantNumeric: "tabular-nums" }}>
                    {t("common.total")}: {CURRENCY_SYMBOL}
                    {cartTotal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
                {cart.map((item) => (
                  <Box
                    key={item.productId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1.5,
                      mb: 1,
                      borderRadius: "12px",
                      background: NJIM.muted,
                      border: `1px solid ${NJIM.border}`,
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500, minWidth: 120 }}>
                      {item.name}
                    </Typography>
                    <Box sx={quantityStepperSx}>
                      <IconButton
                        size="small"
                        onClick={() => handleDecreaseQuantity(item.productId)}
                        aria-label={t("codSalesRep.decreaseQty")}
                        sx={{ width: 40, height: 40 }}
                      >
                        <RemoveRoundedIcon fontSize="small" />
                      </IconButton>
                      <input
                        type="number"
                        min={1}
                        max={item.stock}
                        value={item.quantity}
                        onChange={(e) =>
                          handleSetQuantity(item.productId, e.target.value, item.stock)
                        }
                      />
                      <IconButton
                        size="small"
                        disabled={item.quantity >= item.stock}
                        onClick={() => handleIncreaseQuantity(item.productId, item.stock)}
                        aria-label={t("codSalesRep.increaseQty")}
                        sx={{ width: 40, height: 40 }}
                      >
                        <AddRoundedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography sx={{ fontWeight: 700, minWidth: 90, textAlign: "end", fontVariantNumeric: "tabular-nums" }}>
                      {CURRENCY_SYMBOL}
                      {(item.price * item.quantity).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                    <Button
                      size="small"
                      className="red_btn"
                      onClick={() => handleRemoveItem(item.productId)}
                      sx={{ marginInlineStart: 1, textTransform: "none", minHeight: 40 }}
                    >
                      {t("common.remove")}
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              disabled={cart.length === 0}
              onClick={() => setActiveStep(1)}
              sx={primaryBtnSx}
            >
              {t("common.continue")}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box mt={2}>
          <Typography sx={{ fontSize: "16px", fontWeight: 600, color: NJIM.textPrimary, mb: 1 }}>
            {t("codSalesRep.reviewSubmit")}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: NJIM.textMuted, mb: 2 }}>
            {customer.customerName} ({customer.customerCode}) ·{" "}
            {t("codSalesRep.codPaymentMethod")}
          </Typography>

          <Box
            sx={{
              ...glassCardSx,
              mb: 2,
            }}
          >
            {cart.map((item) => (
              <Box
                key={item.productId}
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ fontSize: "14px", color: NJIM.textSecondary }}>
                  {item.name} × {item.quantity}
                </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {CURRENCY_SYMBOL}
                  {(item.price * item.quantity).toLocaleString("en-US")}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1.5, borderTop: `1px solid ${NJIM.border}` }}>
              <Typography sx={{ fontWeight: 700 }}>{t("common.total")}</Typography>
              <Typography sx={{ fontWeight: 700, color: NJIM.ink }}>
                {CURRENCY_SYMBOL}
                {cartTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
              sx={secondaryBtnSx}
            >
              {t("common.back")}
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleSubmitOrder}
              sx={primaryBtnSx}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submitting ? t("common.loading") : t("codSalesRep.submitOrder")}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CodNewOrderTab;
